/**
 * DORROX — Product Card Hover Crossfade
 * ─────────────────────────────────────
 * Behaviour:
 *   • Desktop: hovering a product card crossfades front → hover image (300ms)
 *   • Mobile / touch: no hover — native slideshow tap behaviour is preserved
 *   • Driven by data-hover-image attribute injected via Liquid snippet
 *
 * Works on:
 *   • Collection grid cards  (product-card custom element)
 *   • PDP featured image     (.dorrox-pdp-hover wrapper added by snippet)
 */

(function () {
  'use strict';

  const FADE_MS = 300;
  const IS_TOUCH = window.matchMedia('(hover: none)').matches;

  /* ── Collection card crossfade ───────────────────────────────────── */

  function initCardHover(card) {
    if (card.dataset.dorroxHover) return; // already initialised
    card.dataset.dorroxHover = 'true';

    const hoverSrc = card.dataset.hoverImage;
    if (!hoverSrc) return;

    // Find the gallery wrapper — first media container inside the card
    const gallery =
      card.querySelector('[ref="cardGallery"]') ||
      card.querySelector('.card-gallery') ||
      card.querySelector('slideshow-component') ||
      card.querySelector('.media');

    if (!gallery) return;

    // Build the hover layer — sits on top of gallery, invisible by default
    const hoverLayer = document.createElement('div');
    hoverLayer.className = 'dorrox-hover-layer';
    hoverLayer.setAttribute('aria-hidden', 'true');

    const img = document.createElement('img');
    img.src = hoverSrc;
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';

    hoverLayer.appendChild(img);
    gallery.style.position = 'relative';
    gallery.appendChild(hoverLayer);

    // Preload on first pointerenter so the crossfade is smooth
    let preloaded = false;

    card.addEventListener('pointerenter', (e) => {
      if (IS_TOUCH || e.pointerType === 'touch') return;
      if (!preloaded) {
        img.loading = 'eager';
        preloaded = true;
      }
      hoverLayer.classList.add('dorrox-hover-layer--visible');
    });

    card.addEventListener('pointerleave', (e) => {
      if (IS_TOUCH || e.pointerType === 'touch') return;
      hoverLayer.classList.remove('dorrox-hover-layer--visible');
    });
  }

  /* ── PDP featured image crossfade ───────────────────────────────── */

  function initPdpHover(wrapper) {
    if (wrapper.dataset.dorroxHover) return;
    wrapper.dataset.dorroxHover = 'true';

    const hoverSrc = wrapper.dataset.hoverImage;
    if (!hoverSrc) return;

    const hoverLayer = document.createElement('div');
    hoverLayer.className = 'dorrox-hover-layer';
    hoverLayer.setAttribute('aria-hidden', 'true');

    const img = document.createElement('img');
    img.src = hoverSrc;
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';

    hoverLayer.appendChild(img);
    wrapper.appendChild(hoverLayer);

    let preloaded = false;

    wrapper.addEventListener('pointerenter', (e) => {
      if (IS_TOUCH || e.pointerType === 'touch') return;
      if (!preloaded) { img.loading = 'eager'; preloaded = true; }
      hoverLayer.classList.add('dorrox-hover-layer--visible');
    });

    wrapper.addEventListener('pointerleave', (e) => {
      if (IS_TOUCH || e.pointerType === 'touch') return;
      hoverLayer.classList.remove('dorrox-hover-layer--visible');
    });
  }

  /* ── Bootstrap ───────────────────────────────────────────────────── */

  function boot() {
    // Collection cards
    document.querySelectorAll('product-card[data-hover-image]').forEach(initCardHover);

    // PDP wrapper
    document.querySelectorAll('.dorrox-pdp-hover[data-hover-image]').forEach(initPdpHover);
  }

  // Run on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run after Shopify section / infinite-scroll renders new cards
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('page:load', boot);     // theme view transitions
  document.addEventListener('page:render', boot);   // fallback

  // MutationObserver for dynamically injected cards (infinite scroll, quick-view)
  const observer = new MutationObserver(() => {
    document.querySelectorAll('product-card[data-hover-image]:not([data-dorrox-hover])').forEach(initCardHover);
    document.querySelectorAll('.dorrox-pdp-hover[data-hover-image]:not([data-dorrox-hover])').forEach(initPdpHover);
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
